import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import L from 'leaflet';
import { FeatureCollection, MultiPolygon, Point, Polygon } from 'geojson';
import { LangChangeEvent, TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BehaviorSubject,
  EMPTY,
  ReplaySubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  forkJoin,
  map,
  shareReplay,
  tap,
} from 'rxjs';
import {
  EbcontGalaxyModule,
  GALAXY_SETTINGS,
  GalaxyInjectorKeys,
  GalaxySettings,
} from '@ebcont/ebcont.galaxy.js';

type SupportedLang = 'DE' | 'EN';

interface CountryProperties {
  ISO_A2_EH: string;
  NAME: string;
  NAME_DE?: string;
  NAME_EN?: string;
  [key: string]: unknown;
}

interface LocationProperties {
  NAME: string;
  NAME_DE?: string;
  NAME_EN?: string;
  [key: string]: unknown;
}

type CountriesGeoJson = FeatureCollection<Polygon | MultiPolygon, CountryProperties>;
type LocationsGeoJson = FeatureCollection<Point, LocationProperties>;

interface CountryMapElement {
  /** ISO A2 code */
  name: string;
  /** number of employees */
  count: number;
}

interface LocationMapElement {
  /** full name of city - always in German */
  name: string;
  /** array of employees */
  children: readonly unknown[];
}

type MapElements = readonly CountryMapElement[] | readonly LocationMapElement[];

const AUSTRIA_CENTER = L.latLng(47.5162, 14.5501);
const AUSTRIA_BOUNDS = L.latLngBounds([AUSTRIA_CENTER]);

const COUNTRY_DEFAULT_ZOOM = 3;
const COUNTRY_BASE_WEIGHT = 0.8;
const COUNTRY_HOVER_WEIGHT = 2;

const LOCATION_DEFAULT_ZOOM = 5;
const LOCATION_LABEL_MIN_ZOOM = 8;
const LOCATION_MARKER_MIN_RADIUS_PX = 3;
const LOCATION_MARKER_MAX_RADIUS_PX = 12;

@Component({
  selector: 'app-leaflet-map',
  standalone: true,
  imports: [HttpClientModule, EbcontGalaxyModule, TranslateModule],
  templateUrl: './leaflet-map.component.html',
  styleUrls: ['./leaflet-map.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LeafletMapComponent implements OnInit, AfterViewInit {
  private readonly httpClient = inject(HttpClient);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  @Output() readonly closeClicked = new EventEmitter<void>();

  private mapElementsValue: MapElements = [];
  @Input()
  set mapElements(mapElements: MapElements) {
    this.mapElementsValue = mapElements ?? [];
    this.mapElementsSubject.next(this.mapElementsValue);
  }
  get mapElements(): MapElements {
    return this.mapElementsValue;
  }

  private leafletMap?: L.Map;

  private countriesLayer?: L.GeoJSON;
  private locationsFeatures: LocationsGeoJson['features'] = [];
  private readonly locationLayerGroup = L.layerGroup();

  private locationMarkers: Array<{ marker: L.CircleMarker; label: string }> = [];
  private locationLabelsVisible = false;

  private centerOnControl?: L.Control;

  private readonly mapReadySubject = new ReplaySubject<void>(1);
  private readonly mapElementsSubject = new BehaviorSubject<MapElements>([]);
  private readonly languageSubject = new BehaviorSubject<SupportedLang>(this.getInitialLanguage());

  private readonly geoData$ = forkJoin({
    countries: this.httpClient.get<CountriesGeoJson>('assets/countries.geojson'),
    locations: this.httpClient.get<LocationsGeoJson>('assets/locations.geojson'),
  }).pipe(
    shareReplay({ bufferSize: 1, refCount: true }),
    catchError((error) => {
      // eslint-disable-next-line no-console
      console.error('GeoJSON load failed:', error);
      return EMPTY;
    }),
  );

  constructor(
    @Inject(GalaxyInjectorKeys.SETTINGS) private readonly settings: GalaxySettings,
  ) {
    this.destroyRef.onDestroy(() => {
      if (this.leafletMap) {
        this.leafletMap.remove();
      }
    });
  }

  get borderColor(): string {
    return this.readCssVariable('--border') || '#d8d3de';
  }

  get fillColor(): string {
    return this.readCssVariable('--surface-rise') || '#ffffff';
  }

  get activeColor(): string {
    return this.readCssVariable('--interactive') || '#6328eb';
  }

  get hoverColor(): string {
    return this.readCssVariable('--active') || this.activeColor;
  }

  ngOnInit(): void {
    this.translateService.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event: LangChangeEvent) => {
        this.languageSubject.next(this.normalizeLanguage(event.lang));
      });

    combineLatest({
      mapReady: this.mapReadySubject,
      geoData: this.geoData$,
      mapElements: this.mapElementsSubject,
      language: this.languageSubject.pipe(distinctUntilChanged()),
    })
      .pipe(
        tap((combined) => {
          this.ensureStaticLayers(combined.geoData.countries, combined.geoData.locations);
          this.updateControlPanels();
        }),
        map((combined) => {
          return { mapElements: combined.mapElements, language: combined.language };
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((combined) => {
        this.render(combined.mapElements, combined.language);
      });
  }

  ngAfterViewInit(): void {
    this.leafletMap = this.createMap('map');
    this.mapReadySubject.next();

    queueMicrotask(() => {
      if (this.leafletMap) {
        this.leafletMap.invalidateSize();
      }
    });
  }

  onCloseButtonClicked(): void {
    this.closeClicked.emit();
  }

  private createMap(containerId: string): L.Map {
    const leafletMap = L.map(containerId, {
      zoom: COUNTRY_DEFAULT_ZOOM,
      minZoom: 2,
      maxZoom: 10,
      attributionControl: false,
      worldCopyJump: true,
    });

    leafletMap.setView(AUSTRIA_CENTER, COUNTRY_DEFAULT_ZOOM);
    L.control.scale().addTo(leafletMap);

    const CenterOnControl = L.Control.extend({
      options: {
        id: 'centerOnAustria',
        position: 'topleft',
      },

      onAdd: (map: L.Map) => {
        const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control');

        const button = L.DomUtil.create('a', 'leaflet-control-center-on', container);
        button.setAttribute('href', '#');
        button.setAttribute('title', 'Center on Austria');
        button.setAttribute('role', 'button');
        button.setAttribute('aria-label', 'Center on Austria');
        button.setAttribute('aria-disabled', 'false');

        const span = L.DomUtil.create('span', '', button);
        span.setAttribute('aria-hidden', 'true');
        span.innerHTML = '⊙';

        L.DomEvent
          .on(button, 'click', L.DomEvent.stop)
          .on(button, 'click', () => {
            if (this.isLocationElements(this.mapElements)) {
              this.fitToAustria(LOCATION_DEFAULT_ZOOM);
            } else {
              this.fitToAustria(COUNTRY_DEFAULT_ZOOM);
            }
          });

        return container;
      },
    });
    this.centerOnControl = new CenterOnControl();
    this.centerOnControl.addTo(leafletMap);

    leafletMap.on('zoomend', () => {
      this.updateLocationLabelVisibility();
    });

    return leafletMap;
  }

  private ensureStaticLayers(countriesGeoJson: CountriesGeoJson, locationsGeoJson: LocationsGeoJson): void {
    if (!this.leafletMap) {
      return;
    }

    if (!this.countriesLayer) {
      this.countriesLayer = this.buildCountriesLayer(countriesGeoJson);
      this.countriesLayer.addTo(this.leafletMap);
    }

    if (this.locationsFeatures.length === 0) {
      this.locationsFeatures = locationsGeoJson.features ?? [];
    }
  }

  private buildCountriesLayer(countriesGeoJson: CountriesGeoJson): L.GeoJSON {
    const currentLanguage = this.languageSubject.value;

    return L.geoJSON(countriesGeoJson as unknown as FeatureCollection, {
      style: {
        weight: COUNTRY_BASE_WEIGHT,
        color: this.borderColor,
        fillColor: this.fillColor,
        fillOpacity: 1,
        opacity: 1,
      },
      onEachFeature: (feature, layer) => {
        const countryProperties = feature?.properties as CountryProperties | undefined;

        layer.bindTooltip(this.getCountryLabel(countryProperties, currentLanguage), { sticky: true });

        layer.on('mouseover', (event: any) => {
          event.target?.setStyle?.({ weight: COUNTRY_HOVER_WEIGHT, color: this.hoverColor });
        });

        layer.on('mouseout', (event: any) => {
          event.target?.setStyle?.({ weight: COUNTRY_BASE_WEIGHT, color: this.borderColor });
        });
      },
    });
  }

  private updateControlPanels(): void {
    if (!this.leafletMap) {
      return;
    }

    const zoomContainer = this.leafletMap.zoomControl.getContainer();
    if (zoomContainer) {
      const zoomInButton = zoomContainer.querySelector<HTMLAnchorElement>('a.leaflet-control-zoom-in');
      if (zoomInButton) {
        const zoomInTitle = this.translateService.instant('leafletMap.zoomIn');
        zoomInButton.setAttribute('title', zoomInTitle);
        zoomInButton.setAttribute('aria-label', zoomInTitle);
      }

      const zoomOutButton = zoomContainer.querySelector<HTMLAnchorElement>('a.leaflet-control-zoom-out');
      if (zoomOutButton) {
        const zoomOutTitle = this.translateService.instant('leafletMap.zoomOut');
        zoomOutButton.setAttribute('title', zoomOutTitle);
        zoomOutButton.setAttribute('aria-label', zoomOutTitle);
      }
    }

    const centerOnContainer = this.centerOnControl?.getContainer();
    if (centerOnContainer) {
      const centerOnButton = centerOnContainer.querySelector<HTMLAnchorElement>('a.leaflet-control-center-on');
      if (centerOnButton) {
        const centerOnTitle = this.translateService.instant('leafletMap.centerOn');
        centerOnButton.setAttribute('title', centerOnTitle);
        centerOnButton.setAttribute('aria-label', centerOnTitle);
      }
    }
  }

  private render(mapElements: MapElements, language: SupportedLang): void {
    if (!this.leafletMap || !this.countriesLayer) {
      return;
    }

    this.updateCountryTooltips(language);

    if (this.isLocationElements(mapElements)) {
      this.renderLocations(mapElements, language);
      this.fitToAustria(LOCATION_DEFAULT_ZOOM);
    } else {
      this.renderCountries(mapElements, language);
      this.fitToAustria(COUNTRY_DEFAULT_ZOOM);
    }
  }

  private renderCountries(countryElements: readonly CountryMapElement[], language: SupportedLang): void {
    if (!this.leafletMap || !this.countriesLayer) {
      return;
    }

    this.locationLayerGroup.clearLayers();
    this.locationMarkers = [];
    this.locationLabelsVisible = false;

    if (this.leafletMap.hasLayer(this.locationLayerGroup)) {
      this.leafletMap.removeLayer(this.locationLayerGroup);
    }

    const employeeCountByCountryIsoCode = new Map<string, number>();
    for (const countryElement of countryElements) {
      employeeCountByCountryIsoCode.set(countryElement.name, countryElement.count);
    }

    const maximumEmployeeCount = Math.max(1, ...countryElements.map((countryElement) => countryElement.count));
    const baseActiveColor = this.activeColor;

    this.countriesLayer.eachLayer((layer) => {
      const countryProperties = (layer as any)?.feature?.properties as CountryProperties | undefined;
      const isoCountryCode = countryProperties?.ISO_A2_EH;
      const employeeCountForCountry = isoCountryCode ? employeeCountByCountryIsoCode.get(isoCountryCode) : undefined;

      const computedFillColor =
        typeof employeeCountForCountry === 'number'
          ? this.toHslShade(baseActiveColor, maximumEmployeeCount, employeeCountForCountry)
          : this.fillColor;

      (layer as any)?.setStyle?.({
        weight: COUNTRY_BASE_WEIGHT,
        color: this.borderColor,
        fillColor: computedFillColor,
        fillOpacity: 1,
        opacity: 1,
      });

      layer.bindTooltip(this.getCountryLabel(countryProperties, language), { sticky: true });
    });
  }

  private renderLocations(locationElements: readonly LocationMapElement[], language: SupportedLang): void {
    if (!this.leafletMap || !this.countriesLayer) {
      return;
    }

    this.countriesLayer.eachLayer((layer) => {
      (layer as any)?.setStyle?.({
        weight: COUNTRY_BASE_WEIGHT,
        color: this.borderColor,
        fillColor: this.fillColor,
        fillOpacity: 0.9,
        opacity: 0.9,
      });

      const countryProperties = (layer as any)?.feature?.properties as CountryProperties | undefined;
      layer.bindTooltip(this.getCountryLabel(countryProperties, language), { sticky: true });
    });

    const nonEmptyLocationElements = locationElements.filter((locationElement) => {
      return locationElement.children.length > 0;
    });

    const employeeCountByLocation = new Map<string, number>();
    for (const locationElement of nonEmptyLocationElements) {
      employeeCountByLocation.set(locationElement.name, locationElement.children.length);
    }

    const maximumEmployeeCount = Math.max(1, ...employeeCountByLocation.values());
    const baseActiveColor = this.activeColor;

    this.locationLayerGroup.clearLayers();
    this.locationMarkers = [];

    for (const locationFeature of this.locationsFeatures) {
      if (locationFeature.geometry?.type !== 'Point') {
        continue;
      }

      const locationProperties = locationFeature.properties;

      const germanLookupKey = (locationProperties.NAME_DE?.trim() || locationProperties.NAME).trim();
      const employeeCountForLocation = employeeCountByLocation.get(germanLookupKey);

      if (employeeCountForLocation === undefined) {
        continue;
      }

      const [longitude, latitude] = locationFeature.geometry.coordinates;
      const latitudeLongitude = L.latLng(latitude, longitude);

      const markerColor = this.toHslShade(baseActiveColor, maximumEmployeeCount, employeeCountForLocation);
      const markerRadiusPixels = this.calculateLocationMarkerRadius(employeeCountForLocation, maximumEmployeeCount);

      const locationMarker = L.circleMarker(latitudeLongitude, {
        radius: markerRadiusPixels,
        weight: 1,
        color: markerColor,
        fillColor: markerColor,
        fillOpacity: 0.75,
      });

      const locationLabel = this.getLocationLabel(locationProperties, language);
      this.bindLocationTooltip(locationMarker, locationLabel, false);

      this.locationMarkers.push({ marker: locationMarker, label: locationLabel });
      this.locationLayerGroup.addLayer(locationMarker);
    }

    if (this.leafletMap.hasLayer(this.locationLayerGroup) === false) {
      this.locationLayerGroup.addTo(this.leafletMap);
    }

    this.updateLocationLabelVisibility(true);
  }

  private fitToAustria(maxZoom: number): void {
    if (!this.leafletMap) {
      return;
    }

    this.leafletMap.setZoom(maxZoom);
    this.leafletMap.fitBounds(AUSTRIA_BOUNDS, { animate: true, maxZoom });
  }

  private updateCountryTooltips(language: SupportedLang): void {
    if (!this.countriesLayer) {
      return;
    }

    this.countriesLayer.eachLayer((layer) => {
      const countryProperties = (layer as any)?.feature?.properties as CountryProperties | undefined;
      layer.bindTooltip(this.getCountryLabel(countryProperties, language), { sticky: true });
    });
  }

  private updateLocationLabelVisibility(forceUpdate = false): void {
    if (!this.leafletMap) {
      return;
    }

    if (this.locationMarkers.length === 0) {
      return;
    }

    const shouldShowLabels = this.leafletMap.getZoom() >= LOCATION_LABEL_MIN_ZOOM;

    if (!forceUpdate && shouldShowLabels === this.locationLabelsVisible) {
      return;
    }

    this.locationLabelsVisible = shouldShowLabels;

    for (const locationMarker of this.locationMarkers) {
      this.bindLocationTooltip(locationMarker.marker, locationMarker.label, shouldShowLabels);

      if (shouldShowLabels) {
        locationMarker.marker.openTooltip();
      }
    }
  }

  private bindLocationTooltip(circleMarker: L.CircleMarker, label: string, isPermanent: boolean): void {
    circleMarker.unbindTooltip();
    circleMarker.bindTooltip(label, {
      permanent: isPermanent,
      direction: 'right',
      offset: L.point(8, 0),
      opacity: 0.9,
    });
  }

  private calculateLocationMarkerRadius(employeeCount: number, maximumEmployeeCount: number): number {
    const ratio = maximumEmployeeCount <= 1 ? 1 : Math.min(1, Math.max(0, employeeCount / maximumEmployeeCount));
    const squareRootScaledRatio = Math.sqrt(ratio);

    return (
      LOCATION_MARKER_MIN_RADIUS_PX +
      (LOCATION_MARKER_MAX_RADIUS_PX - LOCATION_MARKER_MIN_RADIUS_PX) * squareRootScaledRatio
    );
  }

  private getInitialLanguage(): SupportedLang {
    const translateLanguage = this.translateService.currentLang ?? this.translateService.getDefaultLang?.();
    const settingsLanguage = this.settings.get(GALAXY_SETTINGS.LANGUAGE, 'de');

    return this.normalizeLanguage(translateLanguage || settingsLanguage || 'de');
  }

  private normalizeLanguage(language: string | null | undefined): SupportedLang {
    const lowerCasedLanguage = (language ?? '').toLowerCase();

    if (lowerCasedLanguage.startsWith('de')) {
      return 'DE';
    }

    return 'EN';
  }

  private getCountryLabel(countryProperties: CountryProperties | undefined, language: SupportedLang): string {
    if (!countryProperties) {
      return '';
    }

    const localizedName = language === 'DE' ? countryProperties.NAME_DE : countryProperties.NAME_EN;
    const trimmedLocalizedName = (localizedName ?? '').toString().trim();

    if (trimmedLocalizedName.length > 0) {
      return trimmedLocalizedName;
    }

    return countryProperties.NAME;
  }

  private getLocationLabel(locationProperties: LocationProperties, language: SupportedLang): string {
    const localizedName = language === 'DE' ? locationProperties.NAME_DE : locationProperties.NAME_EN;
    const trimmedLocalizedName = (localizedName ?? '').toString().trim();

    if (trimmedLocalizedName.length > 0) {
      return trimmedLocalizedName;
    }

    return locationProperties.NAME;
  }

  private isLocationElements(mapElements: MapElements): mapElements is readonly LocationMapElement[] {
    const firstElement = (mapElements as readonly unknown[])[0] as { children?: unknown } | undefined;

    if (!firstElement) {
      return false;
    }

    return Array.isArray((firstElement as any).children);
  }

  private toHslShade(baseColor: string, maximumEmployeeCount: number, employeeCount: number): string {
    const resolveToHexColor = (color: string): string | null => {
      if (typeof document === 'undefined') {
        return null;
      }

      const trimmedColor = color.trim();
      if (/^#([a-f\d]{6})$/i.test(trimmedColor)) {
        return trimmedColor;
      }

      const context = document.createElement('canvas').getContext('2d');
      if (!context) {
        return null;
      }

      context.fillStyle = '#000000';
      context.fillStyle = trimmedColor;

      const resolvedColor = context.fillStyle;

      if (/^#([a-f\d]{6})$/i.test(resolvedColor)) {
        return resolvedColor;
      }

      const rgbMatch = resolvedColor.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
      if (!rgbMatch) {
        return null;
      }

      const clampToByte = (value: number): number => {
        return Math.max(0, Math.min(255, Number.isFinite(value) ? value : 0));
      };

      const toTwoDigitHex = (value: number): string => {
        return clampToByte(value).toString(16).padStart(2, '0');
      };

      const red = Number(rgbMatch[1]);
      const green = Number(rgbMatch[2]);
      const blue = Number(rgbMatch[3]);

      return `#${toTwoDigitHex(red)}${toTwoDigitHex(green)}${toTwoDigitHex(blue)}`;
    };

    const hexColor = resolveToHexColor(baseColor);
    const hexMatch = hexColor?.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
    if (!hexMatch) {
      return baseColor;
    }

    const red = parseInt(hexMatch[1], 16) / 255;
    const green = parseInt(hexMatch[2], 16) / 255;
    const blue = parseInt(hexMatch[3], 16) / 255;

    const maxChannelValue = Math.max(red, green, blue);
    const minChannelValue = Math.min(red, green, blue);

    let hue = 0;
    let saturation = 0;
    let lightness = (maxChannelValue + minChannelValue) / 2;

    if (maxChannelValue !== minChannelValue) {
      const delta = maxChannelValue - minChannelValue;

      if (lightness > 0.5) {
        saturation = delta / (2 - maxChannelValue - minChannelValue);
      } else {
        saturation = delta / (maxChannelValue + minChannelValue);
      }

      if (maxChannelValue === red) {
        hue = (green - blue) / delta + (green < blue ? 6 : 0);
      } else if (maxChannelValue === green) {
        hue = (blue - red) / delta + 2;
      } else {
        hue = (red - green) / delta + 4;
      }

      hue /= 6;
    }

    const safeMaximum = Math.max(1, maximumEmployeeCount);
    const safeCount = Math.max(1, employeeCount);
    const logarithmicRatio = Math.log(safeCount) / Math.log(safeMaximum);

    const hueDegrees = Math.round(360 * hue);
    const saturationPercent = Math.round(100 * saturation);
    const lightnessPercent = Math.round(100 * (0.95 * lightness + (1 - lightness) * (1 - logarithmicRatio)));

    return `hsl(${hueDegrees}, ${saturationPercent}%, ${lightnessPercent}%)`;
  }

  private readCssVariable(variableName: string): string {
    if (typeof document === 'undefined') {
      return '';
    }

    return getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
  }
}
